import {
  IConfigurationService,
  IStorageService,
  IValidationService,
  AIServiceConfig,
  DEFAULT_AI_CONFIG,
  CONFIG_STORAGE_KEY,
} from './types';

export class QnAConfigService implements IConfigurationService {
  public readonly validationService: IValidationService;

  constructor(
    private readonly storageService: IStorageService,
    validationService: IValidationService,
  ) {
    this.validationService = validationService;
  }

  async getConfig(): Promise<AIServiceConfig> {
    try {
      const configJson = await this.storageService.get(CONFIG_STORAGE_KEY);

      if (!configJson) {
        return { ...DEFAULT_AI_CONFIG };
      }

      const storedConfig = JSON.parse(configJson) as Partial<AIServiceConfig>;

      return this.mergeWithDefaults(storedConfig);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigurationService: Failed to get config', error);
      return { ...DEFAULT_AI_CONFIG };
    }
  }

  async setConfig(config: Partial<AIServiceConfig>): Promise<boolean> {
    try {
      const currentConfig = await this.getConfig();
      const updatedConfig = { ...currentConfig, ...config };

      const validationResult = await this.validateConfig(updatedConfig);
      if (!this.isConfigValid(validationResult)) {
        // eslint-disable-next-line no-console
        console.error('ConfigurationService: Invalid config provided', validationResult);
        return false;
      }

      const configJson = JSON.stringify(updatedConfig, null, 2);
      const success = await this.storageService.set(CONFIG_STORAGE_KEY, configJson);

      return success;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigurationService: Failed to set config', error);
      return false;
    }
  }

  async resetToDefaults(): Promise<void> {
    try {
      await this.storageService.remove(CONFIG_STORAGE_KEY);
      // eslint-disable-next-line no-console
      console.info('ConfigurationService: Config reset to defaults');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigurationService: Failed to reset config', error);
      throw error;
    }
  }

  async exportConfig(): Promise<string> {
    try {
      const config = await this.getConfig();

      const exportData = {
        version: '2.0',
        timestamp: new Date().toISOString(),
        config,
      };

      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigurationService: Failed to export config', error);
      throw error;
    }
  }

  async parseImportConfig(configJson: string): Promise<AIServiceConfig | null> {
    try {
      const importData = JSON.parse(configJson);

      if (!this.isValidImportData(importData)) {
        // eslint-disable-next-line no-console
        console.error('ConfigurationService: Invalid import data format');
        return null;
      }

      const config = importData.config as Partial<AIServiceConfig>;

      return this.mergeWithDefaults(config);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigurationService: Failed to parse import config', error);
      return null;
    }
  }

  async importConfig(configJson: string): Promise<boolean> {
    const parsedConfig = await this.parseImportConfig(configJson);
    if (!parsedConfig) {
      return false;
    }
    return await this.setConfig(parsedConfig);
  }

  async validateFullConfig(config: AIServiceConfig): Promise<{
    isValid: boolean;
    errors: Record<string, string>;
  }> {
    const errors: Record<string, string> = {};

    try {
      const maxContextMessagesResult = this.validationService.validateMaxContextMessages(config.maxContextMessages);
      if (!maxContextMessagesResult.isValid && maxContextMessagesResult.error) {
        errors.maxContextMessages = maxContextMessagesResult.error;
      }

      // Validate backend override parameters only if override is enabled
      if (config.overrideProvider && config.provider) {
        const providerResult = this.validationService.validateProvider(config.provider);
        if (!providerResult.isValid && providerResult.error) {
          errors.provider = providerResult.error;
        }
      }

      if (config.overrideModel && config.model) {
        const modelResult = this.validationService.validateModel(config.model);
        if (!modelResult.isValid && modelResult.error) {
          errors.model = modelResult.error;
        }
      }

      if (config.overrideMaxTokens && config.maxTokens !== undefined) {
        const tokensResult = this.validationService.validateMaxTokens(config.maxTokens);
        if (!tokensResult.isValid && tokensResult.error) {
          errors.maxTokens = tokensResult.error;
        }
      }

      if (config.overrideTemperature && config.temperature !== undefined) {
        const temperatureResult = this.validationService.validateTemperature(config.temperature);
        if (!temperatureResult.isValid && temperatureResult.error) {
          errors.temperature = temperatureResult.error;
        }
      }

      if (config.overrideDocumentId && config.documentId) {
        const documentIdResult = this.validationService.validateDocumentId(config.documentId);
        if (!documentIdResult.isValid && documentIdResult.error) {
          errors.documentId = documentIdResult.error;
        }
      }

      return {
        isValid: Object.keys(errors).length === 0,
        errors,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ConfigurationService: Failed to validate config', error);
      return {
        isValid: false,
        errors: { general: 'Configuration validation error' },
      };
    }
  }

  private mergeWithDefaults(config: Partial<AIServiceConfig>): AIServiceConfig {
    return {
      maxContextMessages: config.maxContextMessages ?? DEFAULT_AI_CONFIG.maxContextMessages,
      overrideProvider: config.overrideProvider ?? DEFAULT_AI_CONFIG.overrideProvider,
      provider: config.provider,
      overrideModel: config.overrideModel ?? DEFAULT_AI_CONFIG.overrideModel,
      model: config.model,
      overrideMaxTokens: config.overrideMaxTokens ?? DEFAULT_AI_CONFIG.overrideMaxTokens,
      maxTokens: config.maxTokens,
      overrideTemperature: config.overrideTemperature ?? DEFAULT_AI_CONFIG.overrideTemperature,
      temperature: config.temperature,
      overrideDocumentId: config.overrideDocumentId ?? DEFAULT_AI_CONFIG.overrideDocumentId,
      documentId: config.documentId,
    };
  }

  private async validateConfig(config: AIServiceConfig): Promise<{ isValid: boolean; errors: Record<string, string> }> {
    return await this.validateFullConfig(config);
  }

  private isConfigValid(validationResult: { isValid: boolean; errors: Record<string, string> }): boolean {
    return validationResult.isValid;
  }

  private isValidImportData(data: unknown): data is { version: string; config: Record<string, unknown> } {
    return (
      typeof data === 'object'
      && data !== null
      && 'version' in data
      && 'config' in data
      && typeof (data as Record<string, unknown>).config === 'object'
    );
  }
}
